// Column value renderers driven by the report metadata. Reuses the existing
// design-system classes (.mono, .badge-*) so reports match the rest of the app.
import React from 'react';
import type { ReportColumn } from './types';
import i18n from '../i18n';

function parseDate(value: unknown, enc?: string): Date | null {
  if (value == null || value === '') return null;
  if (enc === 'epoch-ms') return new Date(Number(value));
  if (enc === 'epoch-s') return new Date(Number(value) * 1000);
  const s = String(value).includes('T') ? String(value) : String(value).replace(' ', 'T') + 'Z';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "just now" / "5m ago" / "3h ago" / "2d ago" — mirrors the AuditLogs convention. */
function timeAgo(d: Date): string {
  const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function formatNumber(value: unknown, col: ReportColumn): string {
  const n = Number(value);
  if (Number.isNaN(n)) return value == null ? '' : String(value);
  const f = col.numberFormat || {};
  let out: string;
  if (f.thousands) {
    out = n.toLocaleString('en-US', {
      minimumFractionDigits: f.decimals ?? 0,
      maximumFractionDigits: f.decimals ?? 3,
    });
  } else {
    out = f.decimals != null ? n.toFixed(f.decimals) : String(n);
  }
  return `${f.prefix ?? ''}${out}${f.suffix ?? ''}`;
}

export function renderCell(col: ReportColumn, raw: unknown): React.ReactNode {
  // valueMap wins for display substitution.
  const mapped = col.valueMap && raw != null ? col.valueMap[String(raw)] : undefined;
  const value = mapped !== undefined ? mapped : raw;

  if (value == null || value === '') return <span className="muted">—</span>;

  const fmt = col.format;

  if (fmt === 'badge') {
    const cls = col.badgeMap?.[String(raw)] || 'badge-info';
    return <span className={`badge ${cls}`}>{String(value)}</span>;
  }

  if (col.type === 'boolean') {
    const on = !!raw;
    return <span className={`badge ${on ? 'badge-success' : 'badge-info'}`}>{on ? (col.truthy ?? i18n.t('reports.formatters.yes')) : (col.falsy ?? i18n.t('reports.formatters.no'))}</span>;
  }

  if (col.type === 'date' || fmt === 'relative-time' || fmt === 'date') {
    const d = parseDate(raw, col.dateEncoding);
    if (!d) return <span className="mono">{String(value)}</span>;
    const full = d.toISOString().replace('T', ' ').replace('.000Z', ' UTC');
    const label = fmt === 'date' ? d.toLocaleString() : timeAgo(d);
    return <span title={full}>{label}</span>;
  }

  if (col.type === 'number') {
    return <span className="mono">{formatNumber(value, col)}</span>;
  }

  if (fmt === 'mono') return <span className="mono">{String(value)}</span>;

  if (fmt === 'link' && typeof value === 'string') {
    return <a href={value} target="_blank" rel="noreferrer">{value}</a>;
  }

  if (Array.isArray(value)) return <span>{value.join(', ')}</span>;

  return <span>{String(value)}</span>;
}
